import { cfgFileName, CONFIG_CATEGORIES, PRACTICE_CFG, PRACTICE_CFG_NAME, rebindCfg, type ConfigCategory } from "@69ly/shared";
import { useState, type FormEvent } from "react";
import { useTranslation } from "react-i18next";
import { Link, useNavigate } from "react-router";
import { CfgTable } from "../components/CfgTable.tsx";
import { Icon } from "../components/Icon.tsx";
import { keysQuery, KeysCard, useRebinds } from "../components/KeysCard.tsx";
import { packBody, PackEditor, type PackFile } from "../components/PackEditor.tsx";
import { Banner, ErrorState, Loading } from "../components/Status.tsx";
import { useCreateConfig, useConfigs } from "../lib/config-queries.ts";
import { useCurrentTeam } from "../lib/current-team.tsx";
import { errorText } from "../lib/errors.ts";

/** Where CS2 reads .cfg files with Steam's default install. */
export const CFG_FOLDER = String.raw`C:\Program Files (x86)\Steam\steamapps\common\Counter-Strike Global Offensive\game\csgo\cfg`;

/** The shelf: the library's configs, the built-in files, and the ways to add one. */
export function ConfigsPage() {
  const { t, i18n } = useTranslation();
  const { scope, team } = useCurrentTeam();
  const list = useConfigs(scope);
  const [category, setCategory] = useState<ConfigCategory | "all">("all");
  if (list.isPending) return <Loading />;
  if (list.error) return <ErrorState message={errorText(t, list.error)} onRetry={() => void list.refetch()} />;
  const shown = list.data.filter((c) => category === "all" || c.category === category);
  const date = (iso: string) => new Date(iso).toLocaleDateString(i18n.language, { day: "numeric", month: "short" });

  return (
    <div className="page configs-page">
      <div className="page-head">
        <div>
          <h1 className="page-title">{t("configs.title")}</h1>
          <p className="muted">{t("configs.intro", { library: team?.name ?? t("nav.personal") })}</p>
        </div>
        <div className="row">
          <Link to="/configs/build" className="btn btn-secondary">
            {t("configs.build")}
          </Link>
          <Link to="/configs/new" className="btn btn-primary">
            <Icon name="plus" />
            {t("configs.add")}
          </Link>
        </div>
      </div>

      <section className="config-builtins" aria-label={t("configs.builtIn")}>
        <Link to="/configs/practice" className="card config-card is-builtin">
          <span className="config-cat">{t("configs.category.practice")}</span>
          <h2 className="panel-title">{t("configs.practiceTitle")}</h2>
          <p className="muted small">{t("configs.practiceHelp")}</p>
        </Link>
        <Link to="/live/setup" className="card config-card is-builtin">
          <span className="config-cat">{t("configs.builtInTag")}</span>
          <h2 className="panel-title">{t("configs.liveTitle")}</h2>
          <p className="muted small">{t("configs.liveHelp")}</p>
        </Link>
      </section>

      <div className="segmented config-cats" role="group" aria-label={t("configs.categoryLabel")}>
        {(["all", ...CONFIG_CATEGORIES] as const).map((c) => (
          <button key={c} type="button" className="seg" aria-pressed={category === c} onClick={() => setCategory(c)}>
            {c === "all" ? t("configs.allCategories") : t(`configs.category.${c}`)}
          </button>
        ))}
      </div>

      {shown.length === 0 ? (
        <div className="card empty-card-inline">
          <p>{list.data.length === 0 ? t("configs.empty") : t("configs.noMatch")}</p>
        </div>
      ) : (
        <ul className="config-list">
          {shown.map((c) => (
            <li key={c.id}>
              <Link to={`/configs/${c.id}`} className="card config-card">
                <span className="config-cat">{t(`configs.category.${c.category}`)}</span>
                <h2 className="panel-title">{c.title}</h2>
                {c.description && <p className="muted small config-desc">{c.description}</p>}
                <p className="small config-meta">
                  {t("configs.meta", { name: c.author.name, version: c.version, lines: c.lines, date: date(c.updatedAt) })}
                  {c.fileCount > 1 && <span className="pack-main-tag">{t("configs.packTag", { count: c.fileCount })}</span>}
                  {c.warnings > 0 && <span className="cfg-flag is-warn">{t("configs.toCheck", { count: c.warnings })}</span>}
                </p>
              </Link>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

/** Add a config: one file or a pack (a main file and the files it runs); read it before saving. */
export function NewConfigPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { scope, team } = useCurrentTeam();
  const create = useCreateConfig();
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [category, setCategory] = useState<ConfigCategory>("crosshair");
  const [files, setFiles] = useState<PackFile[]>([{ name: "", text: "" }]);
  const [active, setActive] = useState(0);
  const mainHint = cfgFileName(title);
  const names = [files[0].name.trim() || mainHint, ...files.slice(1).map((f) => f.name)];
  const ready = !!title.trim() && files.every((f, i) => f.text.trim() && (i === 0 || f.name.trim()));

  const changeFiles = (next: PackFile[]) => {
    setFiles(next);
    // A picked file names the config until a title is typed.
    if (!title && next[0]?.name) setTitle(next[0].name.replace(/\.cfg$/i, "").replace(/[_-]+/g, " "));
  };
  const submit = (e: FormEvent) => {
    e.preventDefault();
    create.mutate({ scope, title, description, category, overrides: {}, ...packBody(files) }, { onSuccess: (c) => navigate(`/configs/${c.id}`, { replace: true }) });
  };

  return (
    <form className="page config-form" onSubmit={submit}>
      <div>
        <Link to="/configs" className="back-link">
          <Icon name="chevronLeft" size={16} />{t("configs.title")}
        </Link>
        <h1 className="page-title">{t("configs.addTitle", { library: team?.name ?? t("nav.personal") })}</h1>
      </div>
      {create.error && <Banner tone="warning">{errorText(t, create.error)}</Banner>}
      <div className="columns">
        <div className="column-main form-main">
          <section className="card">
            <PackEditor files={files} onChange={changeFiles} active={active} onActive={setActive} mainHint={mainHint} />
          </section>
          {files[active]?.text.trim() && (
            <section className="card">
              <h2 className="section-title">{files.length > 1 ? t("configs.readFile", { name: names[active] }) : t("configs.readIt")}</h2>
              <CfgTable text={files[active].text} pack={{ names, open: (n) => setActive(Math.max(0, names.findIndex((x) => x.toLowerCase() === n.toLowerCase()))) }} />
            </section>
          )}
        </div>
        <aside className="column-side">
          <section className="card">
            <label className="field">
              <span>{t("form.titleLabel")}</span>
              <input value={title} onChange={(e) => setTitle(e.target.value)} maxLength={60} required />
            </label>
            <label className="field">
              <span>{t("configs.categoryLabel")}</span>
              <select value={category} onChange={(e) => setCategory(e.target.value as ConfigCategory)}>
                {CONFIG_CATEGORIES.map((c) => (
                  <option key={c} value={c}>
                    {t(`configs.category.${c}`)}
                  </option>
                ))}
              </select>
            </label>
            <label className="field">
              <span>{t("configs.description")}</span>
              <textarea rows={3} value={description} maxLength={500} onChange={(e) => setDescription(e.target.value)} />
            </label>
            <button type="submit" className="btn btn-primary" disabled={!ready || create.isPending}>
              {t("configs.save")}
            </button>
          </section>
        </aside>
      </div>
    </form>
  );
}

/** The practice config, built in: read it, download it. */
const PRACTICE_FILES = [{ name: PRACTICE_CFG_NAME, text: PRACTICE_CFG }];

export function PracticeConfigPage() {
  const { t } = useTranslation();
  const [keys, setKeys] = useRebinds("69ly.keys.practice");
  const text = rebindCfg(PRACTICE_CFG, keys);
  return (
    <div className="page config-detail">
      <div>
        <Link to="/configs" className="back-link">
          <Icon name="chevronLeft" size={16} />{t("configs.title")}
        </Link>
        <p className="eyebrow">{t("configs.category.practice")}</p>
        <h1 className="page-title">{t("configs.practiceTitle")}</h1>
        <p className="muted">{t("configs.practiceIntro")}</p>
      </div>
      <div className="columns">
        <div className="column-main">
          <section className="card">
            <CfgTable text={text} />
          </section>
        </div>
        <aside className="column-side">
          <KeysCard files={PRACTICE_FILES} value={keys} onChange={setKeys} />
          <InstallCard fileName={PRACTICE_CFG_NAME} downloadUrl={`/api/configs/practice${keysQuery(keys, "?")}`} text={text} />
        </aside>
      </div>
    </div>
  );
}

/** Download, copy, and where the file goes. A pack downloads as a .zip to unzip in the same folder. */
export function InstallCard({ fileName, downloadUrl, text, pack }: { fileName: string; downloadUrl: string; text: string; pack?: { count: number; fileUrl: string; fileName: string } }) {
  const { t } = useTranslation();
  const exec = `exec ${fileName.replace(/\.cfg$/, "")}`;
  return (
    <section className="card config-install">
      <h2 className="panel-title">{t("configs.useIt")}</h2>
      <div className="row">
        <a className="btn btn-primary" href={downloadUrl} download={pack ? fileName.replace(/\.cfg$/, ".zip").split("/").pop() : fileName}>
          {pack ? t("configs.downloadPack", { count: pack.count }) : t("configs.download")}
        </a>
        <CopyTextButton text={text} />
      </div>
      {pack && (
        <p className="small">
          <a href={pack.fileUrl} download={pack.fileName.split("/").pop()}>
            {t("configs.downloadOne", { name: pack.fileName })}
          </a>
        </p>
      )}
      <ol className="config-steps small">
        <li>
          {pack ? t("configs.step1Pack") : t("configs.step1")}
          <code className="config-path">{CFG_FOLDER}</code>
        </li>
        <li>
          {t("configs.step2")}
          <span className="command">
            <code>{exec}</code>
          </span>
        </li>
      </ol>
      <p className="muted small">{t("configs.autoexec", { exec })}</p>
    </section>
  );
}

function CopyTextButton({ text }: { text: string }) {
  const { t } = useTranslation();
  const [done, setDone] = useState(false);
  return (
    <button
      type="button"
      className="btn btn-secondary"
      onClick={async () => {
        try {
          await navigator.clipboard.writeText(text);
          setDone(true);
          setTimeout(() => setDone(false), 2000);
        } catch {
          // No clipboard: the text is on the page.
        }
      }}
    >
      <Icon name={done ? "check" : "copy"} />
      {done ? t("mapView.copied") : t("configs.copyAll")}
    </button>
  );
}
